/* eslint-disable react-hooks/exhaustive-deps */
import { Link } from "react-router-dom";
import React, { useEffect, useState } from "react";
import { SearchList } from "../../component/Functions";
import { _PAGE_SIZE_ } from "../../../../middlewares/other/tools";
import { useCompanyContext } from "../../../../context/company/companyContext";
import { useAdminContext } from "../../../../context/admin/context/AdminContext";
import { pushOneCompany } from "../../../../middlewares/sysNavigation/SysNavigation";
import {
  Modal,
  MyButton,
  MySearch,
  Pagination,
} from "../../component/Component";

const Overview = () => {
  const [page, setPage] = useState(1);
  const {
    sysAdmin: { role },
  } = useAdminContext();

  const [companiesList, setCompanies] = useState([]);
  const [pageSize, setPageSize] = useState(_PAGE_SIZE_);
  const { companies, getAll, count } = useCompanyContext();

  useEffect(() => {
    if (!companies) getAll(page, pageSize);
    else setCompanies(companies);
  }, [companies]);

  return (
    <div className="h-full overflow-y-auto bg-gray-50 p-4 rounded-b-xl">
      <div className="mx-auto max-w-6xl space-y-5">
        {/* Header / Toolbar */}
        <div className="relative">
          <div className="pointer-events-none absolute -inset-x-6 -top-6 h-24 rounded-3xl bg-gradient-to-r from-emerald-400/15 via-amber-400/15 to-fuchsia-400/15 blur-2xl -z-10" />
          <div className="relative rounded-2xl border border-gray-200 bg-white/90 shadow-sm backdrop-blur">
            <div className="flex flex-col gap-3 p-4 md:flex-row md:items-center md:justify-between">
              <div>
                <h1 className="text-base font-semibold tracking-tight text-gray-900">
                  Companies
                </h1>
                <p className="mt-0.5 text-xs text-gray-600">
                  Manage partner companies and their details.
                </p>
              </div>

              <div className="flex w-full flex-col gap-3 sm:flex-row sm:items-center sm:justify-end">
                <div className="flex items-center justify-end gap-2">
                  <span className="inline-flex items-center gap-2 rounded-full bg-gray-50 px-2.5 py-1 text-[11px] font-medium text-gray-700 ring-1 ring-gray-200">
                    Total
                    <span className="grid place-items-center rounded-md bg-gray-900 px-2 py-0.5 text-white">
                      {count ?? 0}
                    </span>
                  </span>

                  {["admin", "super_admin"].includes(role) ? (
                    <Link to={"/company/add"}>
                      <MyButton className="rounded-lg bg-gradient-to-br from-gray-900 to-gray-700 px-3 py-2 text-xs font-semibold hover:from-black hover:to-gray-800">
                        Add Company
                      </MyButton>
                    </Link>
                  ) : null}
                </div>

                <div className="sm:min-w-[280px]">
                  <MySearch
                    onChange={(e) =>
                      setCompanies(SearchList(e?.target?.value, "name", companies))
                    }
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Table */}
        <div className="overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm">
          <div className="relative max-h-[65vh] overflow-auto">
            <table className="min-w-full text-sm">
              <thead className="sticky top-0 z-10 bg-white/90 backdrop-blur">
                <tr className="text-left text-xs uppercase tracking-wide text-gray-500">
                  <th className="px-4 py-3 font-semibold">Name</th>
                  <th className="px-4 py-3 font-semibold">Website</th>
                  <th className="px-4 py-3 font-semibold text-center">Operations</th>
                </tr>
              </thead>
              <tbody>
                {companiesList && companiesList?.length > 0 ? (
                  companiesList?.map((cmp) => (
                    <tr
                      key={cmp?.company_id}
                      className="group border-t border-gray-100 hover:bg-gray-50/60"
                    >
                      <td className="px-4 py-3 align-middle">
                        <div className="flex items-center gap-3">
                          <div className="grid h-8 w-8 place-items-center rounded-lg bg-gray-900 text-[10px] font-bold text-white shadow-sm">
                            {(cmp?.name || "CO").slice(0, 2).toUpperCase()}
                          </div>
                          <div className="min-w-0">
                            <div className="truncate font-semibold text-gray-900">
                              {cmp?.name}
                            </div>
                            <div className="truncate text-[11px] text-gray-500">
                              ID: {cmp?.company_id}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3 align-middle">
                        <span className="inline-flex max-w-[440px] truncate rounded-md bg-gray-50 px-2 py-1 text-xs text-gray-700 ring-1 ring-gray-200">
                          {cmp?.website || "—"}
                        </span>
                      </td>
                      <td className="px-4 py-3 align-middle">
                        <div className="flex justify-center">
                          <CallToAction
                            company={cmp}
                            name={cmp?.name}
                            company_id={cmp?.company_id}
                          />
                        </div>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td className="px-4 py-6 text-center text-gray-500" colSpan={3}>
                      No companies found.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          <div className="border-t border-gray-100 px-4 py-3">
            <div className="flex items-center justify-end">
              <Pagination
                page={page}
                setPage={setPage}
                pageSize={pageSize}
                total_items={count}
                setPageSize={setPageSize}
                callbackFunction={(p, size) => getAll(p, size)}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

/**
 * @author Ntavigwa Bashombe
 * @param { company_id, name, company }
 * @returns
 */
const CallToAction = ({ company_id, name, company }) => {
  const [showModal, setShowModal] = useState(false);
  const { companies, setCompany, deleteCompany, setCompanies } =
    useCompanyContext();

  const deleteOneCompanyById = async (cid) => {
    const { status } = await deleteCompany(cid, company?.owner_id);
    if (status === 200) {
      setShowModal(false);
      setCompanies(
        companies ? companies?.filter((c) => c?.company_id !== cid) : null
      );
    }
  };

  return (
    <>
      <Modal
        props={{
          name: name,
          showModal: showModal,
          setShowModal: () => setShowModal(false),
          delete: () => deleteOneCompanyById(company_id),
        }}
      />

      <div
        className={[
          "inline-flex w-full max-w-[720px] flex-wrap items-center justify-center gap-2",
          "rounded-xl border border-gray-200 bg-white/70 px-2 py-2 shadow-sm",
        ].join(" ")}
      >
        {/* <Link to={"/company/view"}>
          <MyButton
            className="rounded-lg bg-gradient-to-br from-emerald-700 to-emerald-600 px-3 py-2 text-xs font-semibold hover:from-emerald-800 hover:to-emerald-700"
            onClick={() => pushOneCompany(company)}
          >
            View
          </MyButton>
        </Link> */}

        <Link to={"/company/dashboard"}>
          <MyButton
            className="rounded-lg bg-gradient-to-br from-blue-700 to-blue-600 px-3 py-2 text-xs font-semibold hover:from-blue-800 hover:to-blue-700"
            onClick={() => pushOneCompany(company)}
          >
            Dashboard
          </MyButton>
        </Link>

        <Link to={"/company/edit"}>
          <MyButton
            className="rounded-lg bg-gradient-to-br from-gray-900 to-gray-700 px-3 py-2 text-xs font-semibold hover:from-black hover:to-gray-800"
            onClick={() => {
              pushOneCompany(company);
              setCompany(null);
            }}
          >
            Edit
          </MyButton>
        </Link>

        <MyButton
          className="rounded-lg bg-gradient-to-br from-red-700 to-red-600 px-3 py-2 text-xs font-semibold hover:from-red-800 hover:to-red-700"
          onClick={() => setShowModal(true)}
        >
          Delete
        </MyButton>
      </div>
    </>
  );
};

export default Overview;
